import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/Button';
import type { TemplateListItem } from './types';

// ── Template Switcher Component ───────────────────────────────────────────────

interface TemplateSwitcherProps {
  templates: TemplateListItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNew: (name: string) => Promise<any>;
  onDelete: (id: string) => Promise<any>;
  onSetDefault: (id: string) => Promise<any>;
  isAdmin: boolean;
  isLoading?: boolean;
}

export default function TemplateSwitcher({
  templates, selectedId, onSelect, onNew, onDelete, onSetDefault, isAdmin, isLoading = false
}: TemplateSwitcherProps) {
  const [showNewModal, setShowNewModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleNewTemplate = async () => {
    if (!newName.trim()) { toast.error('Nome obbligatorio'); return; }
    try {
      setIsCreating(true);
      await onNew(newName);
      setShowNewModal(false);
      setNewName('');
    } catch (e: any) {
      toast.error(e.message || 'Errore creazione');
    } finally {
      setIsCreating(false);
    }
  };

  const selectedTemplate = templates.find(t => t.id === selectedId);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
      <select
        value={selectedId ?? ''}
        onChange={e => onSelect(e.target.value)}
        disabled={isLoading}
        style={{
          padding: '0.35rem 0.5rem',
          fontSize: 'var(--text-sm)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-interactive)',
          background: 'var(--glass-bg-card)',
          color: 'var(--color-text)',
          cursor: 'pointer',
          flex: 1,
          minWidth: 180,
        }}
      >
        {templates.map(t => (
          <option key={t.id} value={t.id}>
            {t.name} {t.isDefault ? '● Attivo' : ''}
          </option>
        ))}
      </select>

      {isAdmin && (
        <>
          <Button
            variant="ghost"
            size="sm"
            icon={<Plus size={12} />}
            onClick={() => setShowNewModal(true)}
            title="Crea nuovo template"
          >
            Nuovo
          </Button>

          {selectedTemplate && !selectedTemplate.isDefault && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSetDefault(selectedId!)}
              title="Imposta come predefinito"
            >
              Imposta default
            </Button>
          )}

          {selectedTemplate && !selectedTemplate.isUsed && !selectedTemplate.isDefault && (
            <Button
              variant="ghost"
              size="sm"
              icon={<Trash2 size={12} />}
              onClick={() => {
                if (confirm(`Eliminare "${selectedTemplate.name}"?`)) {
                  onDelete(selectedId!).catch(e => toast.error(e.message));
                }
              }}
            >
              Elimina
            </Button>
          )}
        </>
      )}

      {showNewModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }} onClick={() => setShowNewModal(false)}>
          <div style={{
            background: 'var(--glass-bg-card)', padding: '1rem', borderRadius: 'var(--radius-card)',
            minWidth: 300,
          }} onClick={e => e.stopPropagation()}>
            <label style={{ fontSize: 'var(--text-sm)', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
              Nome template
            </label>
            <input
              autoFocus
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleNewTemplate()}
              placeholder="Es: Standard, Premium, Wedding..."
              style={{
                width: '100%', padding: '0.5rem', border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 'var(--radius-interactive)', marginBottom: '1rem',
                background: 'var(--glass-bg-card)', color: 'var(--color-text)',
              }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <Button variant="secondary" size="sm" onClick={() => setShowNewModal(false)}>
                Annulla
              </Button>
              <Button variant="primary" size="sm" onClick={handleNewTemplate} loading={isCreating}>
                Crea
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
